import { memo, type ReactNode } from "react";

import { cn } from "@/lib/utils/cn";
import type { EventStatus } from "@/types/domain";

interface BadgeProps {
  children: ReactNode;
  className?: string;
  variant?: "primary" | "success" | "warning" | "danger" | "neutral";
}

const variantClasses = {
  primary: "badge-primary",
  success:
    "badge bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  warning: "badge-warning",
  danger: "badge-danger",
  neutral:
    "badge bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400",
} as const;

const Badge = memo(function Badge({
  children,
  className,
  variant = "neutral",
}: BadgeProps) {
  return (
    <span className={cn(variantClasses[variant], className)}>{children}</span>
  );
});

export default Badge;

// ─── Convenience: EventStatus badge ──────────────────────────────
const STATUS_VARIANT: Record<
  EventStatus,
  NonNullable<BadgeProps["variant"]>
> = {
  scheduled: "primary",
  live: "success",
  completed: "neutral",
  postponed: "warning",
  cancelled: "danger",
};

const STATUS_DOT: Record<EventStatus, string> = {
  scheduled: "",
  live: "animate-pulse",
  completed: "",
  postponed: "",
  cancelled: "",
};

interface EventStatusBadgeProps {
  status: EventStatus;
}

export const EventStatusBadge = memo(function EventStatusBadge({
  status,
}: EventStatusBadgeProps) {
  return (
    <Badge variant={STATUS_VARIANT[status]}>
      {status === "live" && (
        <span
          className={cn(
            "mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current",
            STATUS_DOT[status],
          )}
          aria-hidden
        />
      )}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
});
